import React, { useState } from 'react';
import {
    Box,
    Card,
    Typography,
    Stack,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    TextField,
    Chip,
    IconButton
} from '@mui/material';
import { Add, Search, Edit } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePromotions } from 'hooks/usePromotions';
import { format } from 'date-fns';

const PromotionList = () => {
    const navigate = useNavigate();
    const { data: promotionsData, isLoading } = usePromotions();
    const [searchTerm, setSearchTerm] = useState('');

    // Assuming the API returns `{ success: true, data: [...] }`
    // Wait, usePromotions queryFn already extracts res.data. Let's handle if it returns { data, success } or just the array
    const promotions = Array.isArray(promotionsData?.data) ? promotionsData.data : Array.isArray(promotionsData) ? promotionsData : [];

    const filteredPromotions = promotions.filter((promo) => {
        if (!searchTerm) return true;
        return (
            promo.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            promo.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    const getStatusChip = (promo) => {
        if (!promo.isActive) return <Chip label="Inactive" color="default" size="small" />;
        const expiry = promo.endDate || promo.expiryDate; // Fallback for legacy
        if (!expiry) return <Chip label="Active" color="success" size="small" />;
        const isExpired = new Date(expiry) < new Date();
        if (isExpired) return <Chip label="Expired" color="error" size="small" />;
        return <Chip label="Active" color="success" size="small" />;
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={{ xs: 2, md: 4 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={800} color="primary.main">
                    Promotions & Coupons
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/admin/promotions/create')} size="large">
                    Create Promotion
                </Button>
            </Stack>

            <Card sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Search by promo code or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        )
                    }}
                />
            </Card>

            <Card sx={{ p: 2, borderRadius: 3 }}>
                <TableContainer component={Paper} elevation={0}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                                <TableCell>
                                    <strong>Promo Code</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Discount</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Type</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Validity / Usage</strong>
                                </TableCell>
                                <TableCell>
                                    <strong>Status</strong>
                                </TableCell>
                                <TableCell align="right">
                                    <strong>Actions</strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPromotions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        No promotions found. Click "Create Promotion" to generate a coupon.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredPromotions.map((promo) => (
                                    <TableRow key={promo._id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold" color="primary">
                                                {promo.code}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                noWrap
                                                sx={{ maxWidth: 200, display: 'block' }}
                                            >
                                                {promo.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {promo.discountType === 'percentage'
                                                    ? `${promo.discountValue}%`
                                                    : `₦${promo.discountValue}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>{promo.type || 'Platform'}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" display="block">
                                                Exp:{' '}
                                                {promo.endDate || promo.expiryDate
                                                    ? format(new Date(promo.endDate || promo.expiryDate), 'MMM dd, yyyy')
                                                    : 'No Expiry'}
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                Used:{' '}
                                                {promo.maxUsage ? `${promo.usedCount || 0} / ${promo.maxUsage}` : promo.usedCount || 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{getStatusChip(promo)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton color="primary" onClick={() => navigate(`/admin/promotions/${promo._id}`)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </Box>
    );
};

export default PromotionList;
